import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Progress } from '../../../components/ui';
import { 
  User, 
  Mail, 
  Eye, 
  MousePointer, 
  MessageSquare, 
  Target,
  Clock,
  TrendingUp,
  TrendingDown,
  MapPin,
  Smartphone,
  Monitor,
  Calendar,
  Activity,
  Zap,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Filter,
  Search
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';

// Simple tabs components (same as used in EngagementHeatmap)
const Tabs: React.FC<{ children: React.ReactNode; defaultValue?: string; value?: string; onValueChange?: (value: string) => void; className?: string }> = ({ children, className }) => (
  <div className={className}>{children}</div>
);

const TabsList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground ${className}`}>
    {children}
  </div>
);

const TabsTrigger: React.FC<{ children: React.ReactNode; value: string; className?: string }> = ({ children, className }) => (
  <button className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm ${className}`}>
    {children}
  </button>
);

const TabsContent: React.FC<{ children: React.ReactNode; value: string; className?: string }> = ({ children, className }) => (
  <div className={`mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}>
    {children}
  </div>
);

interface JourneyTouchpoint {
  id: string;
  recipientId: string;
  campaignId: string;
  type: 'email_sent' | 'email_opened' | 'link_clicked' | 'reply_received' | 'conversion' | 'unsubscribe' | 'bounce';
  timestamp: string;
  metadata: {
    emailId?: string;
    linkUrl?: string;
    deviceType?: string;
    location?: string;
    conversionValue?: number;
  };
  context: {
    campaignName: string;
    subjectLine?: string;
  };
  engagement: {
    score: number;
    duration?: number;
  };
}

interface RecipientJourney {
  recipientId: string;
  recipientInfo: {
    email: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    title?: string;
    segment?: string;
  };
  journey: {
    startDate: string;
    lastActivity: string;
    totalDuration: number;
    status: 'active' | 'converted' | 'unsubscribed' | 'inactive';
    stage: 'awareness' | 'interest' | 'consideration' | 'decision' | 'customer' | 'advocate';
  };
  touchpoints: JourneyTouchpoint[];
  metrics: {
    totalEmails: number;
    emailsOpened: number;
    linksClicked: number;
    repliesSent: number;
    conversions: number;
    engagementScore: number;
    responseTime: number;
  };
  patterns: {
    preferredTimes: {
      hours: number[];
      days: number[];
    };
    devicePreference: 'mobile' | 'desktop' | 'mixed';
    engagementTrend: 'increasing' | 'decreasing' | 'stable';
  };
  predictions: {
    conversionLikelihood: number;
    nextBestAction: string;
    optimalNextContact: string;
    churnRisk: 'low' | 'medium' | 'high';
  };
}

interface RecipientJourneyProps {
  recipientId?: string;
  journeys?: RecipientJourney[];
  showFilters?: boolean;
  showPredictions?: boolean;
}

const RecipientJourney: React.FC<RecipientJourneyProps> = ({
  recipientId,
  journeys = [],
  showFilters = true,
  showPredictions = true
}) => {
  const [selectedJourney, setSelectedJourney] = useState<RecipientJourney | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'analytics' | 'segments'>('timeline');
  const [filterStage, setFilterStage] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for demonstration
  const mockJourneys: RecipientJourney[] = [
    {
      recipientId: 'recipient-1',
      recipientInfo: {
        email: 'john.doe@acme.com',
        firstName: 'John',
        lastName: 'Doe',
        company: 'Acme Corp',
        title: 'VP of Sales',
        segment: 'Enterprise'
      },
      journey: {
        startDate: '2024-01-01T10:00:00Z',
        lastActivity: '2024-01-15T14:30:00Z',
        totalDuration: 14,
        status: 'converted',
        stage: 'customer'
      },
      touchpoints: [
        {
          id: 'tp-1',
          recipientId: 'recipient-1',
          campaignId: 'camp-1',
          type: 'email_sent',
          timestamp: '2024-01-01T10:00:00Z',
          metadata: { emailId: 'email-1', deviceType: 'desktop' },
          context: { campaignName: 'Welcome Series', subjectLine: 'Welcome to our platform!' },
          engagement: { score: 10 }
        },
        {
          id: 'tp-2',
          recipientId: 'recipient-1',
          campaignId: 'camp-1',
          type: 'email_opened',
          timestamp: '2024-01-01T10:15:00Z',
          metadata: { emailId: 'email-1', deviceType: 'mobile', location: 'New York, NY' },
          context: { campaignName: 'Welcome Series', subjectLine: 'Welcome to our platform!' },
          engagement: { score: 25, duration: 45 }
        },
        {
          id: 'tp-3',
          recipientId: 'recipient-1',
          campaignId: 'camp-1',
          type: 'link_clicked',
          timestamp: '2024-01-01T10:20:00Z',
          metadata: { linkUrl: 'https://example.com/demo', deviceType: 'mobile' },
          context: { campaignName: 'Welcome Series' },
          engagement: { score: 50, duration: 120 }
        },
        {
          id: 'tp-4',
          recipientId: 'recipient-1',
          campaignId: 'camp-2',
          type: 'reply_received',
          timestamp: '2024-01-02T09:30:00Z',
          metadata: { deviceType: 'desktop' },
          context: { campaignName: 'Follow-up Series', subjectLine: 'Quick question about your demo' },
          engagement: { score: 75 }
        },
        {
          id: 'tp-5',
          recipientId: 'recipient-1',
          campaignId: 'camp-3',
          type: 'conversion',
          timestamp: '2024-01-15T14:30:00Z',
          metadata: { conversionValue: 5000, deviceType: 'desktop' },
          context: { campaignName: 'Closing Series' },
          engagement: { score: 100 }
        }
      ],
      metrics: {
        totalEmails: 8,
        emailsOpened: 6,
        linksClicked: 4,
        repliesSent: 2,
        conversions: 1,
        engagementScore: 85,
        responseTime: 18.5
      },
      patterns: {
        preferredTimes: { hours: [9, 10, 14], days: [1, 2, 3] },
        devicePreference: 'mixed',
        engagementTrend: 'increasing'
      },
      predictions: {
        conversionLikelihood: 0.95,
        nextBestAction: 'Schedule onboarding call',
        optimalNextContact: '2024-01-16T10:00:00Z',
        churnRisk: 'low'
      }
    },
    {
      recipientId: 'recipient-2',
      recipientInfo: {
        email: 'sarah.smith@techco.com',
        firstName: 'Sarah',
        lastName: 'Smith',
        company: 'TechCo',
        title: 'Marketing Director',
        segment: 'Mid-Market'
      },
      journey: {
        startDate: '2024-01-05T14:00:00Z',
        lastActivity: '2024-01-12T16:45:00Z',
        totalDuration: 7,
        status: 'active',
        stage: 'consideration'
      },
      touchpoints: [
        {
          id: 'tp-6',
          recipientId: 'recipient-2',
          campaignId: 'camp-1',
          type: 'email_sent',
          timestamp: '2024-01-05T14:00:00Z',
          metadata: { emailId: 'email-2', deviceType: 'desktop' },
          context: { campaignName: 'Product Demo Series', subjectLine: 'See how we can help TechCo' },
          engagement: { score: 10 }
        },
        {
          id: 'tp-7',
          recipientId: 'recipient-2',
          campaignId: 'camp-1',
          type: 'email_opened',
          timestamp: '2024-01-05T14:30:00Z',
          metadata: { emailId: 'email-2', deviceType: 'mobile', location: 'San Francisco, CA' },
          context: { campaignName: 'Product Demo Series', subjectLine: 'See how we can help TechCo' },
          engagement: { score: 25, duration: 60 }
        },
        {
          id: 'tp-8',
          recipientId: 'recipient-2',
          campaignId: 'camp-1',
          type: 'link_clicked',
          timestamp: '2024-01-12T16:45:00Z',
          metadata: { linkUrl: 'https://example.com/case-study', deviceType: 'desktop' },
          context: { campaignName: 'Product Demo Series' },
          engagement: { score: 50, duration: 180 }
        }
      ],
      metrics: {
        totalEmails: 3,
        emailsOpened: 2,
        linksClicked: 1,
        repliesSent: 0,
        conversions: 0,
        engagementScore: 65,
        responseTime: 0
      },
      patterns: {
        preferredTimes: { hours: [14, 16], days: [1, 5] },
        devicePreference: 'mixed',
        engagementTrend: 'stable'
      },
      predictions: {
        conversionLikelihood: 0.45,
        nextBestAction: 'Send case study',
        optimalNextContact: '2024-01-15T14:00:00Z',
        churnRisk: 'medium'
      }
    }
  ];

  const activeJourneys = journeys.length > 0 ? journeys : mockJourneys;
  const currentJourney = selectedJourney || activeJourneys[0];

  useEffect(() => {
    if (recipientId) {
      const journey = activeJourneys.find(j => j.recipientId === recipientId);
      setSelectedJourney(journey || null);
    }
  }, [recipientId, activeJourneys]);

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'awareness': return 'bg-gray-500';
      case 'interest': return 'bg-blue-500';
      case 'consideration': return 'bg-yellow-500';
      case 'decision': return 'bg-orange-500';
      case 'customer': return 'bg-green-500';
      case 'advocate': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-500';
      case 'converted': return 'bg-green-500';
      case 'unsubscribed': return 'bg-red-500';
      case 'inactive': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getTouchpointIcon = (type: string) => {
    switch (type) {
      case 'email_sent': return Mail;
      case 'email_opened': return Eye;
      case 'link_clicked': return MousePointer;
      case 'reply_received': return MessageSquare;
      case 'conversion': return Target;
      case 'unsubscribe': return AlertTriangle;
      case 'bounce': return AlertTriangle;
      default: return Activity;
    }
  };

  const getTouchpointColor = (type: string) => {
    switch (type) {
      case 'email_sent': return 'text-blue-500';
      case 'email_opened': return 'text-green-500';
      case 'link_clicked': return 'text-purple-500';
      case 'reply_received': return 'text-orange-500';
      case 'conversion': return 'text-red-500';
      case 'unsubscribe': return 'text-gray-500';
      case 'bounce': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getChurnRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredJourneys = activeJourneys.filter(journey => {
    const matchesStage = filterStage === 'all' || journey.journey.stage === filterStage;
    const matchesStatus = filterStatus === 'all' || journey.journey.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      journey.recipientInfo.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      journey.recipientInfo.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${journey.recipientInfo.firstName} ${journey.recipientInfo.lastName}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStage && matchesStatus && matchesSearch;
  });

  const TimelineView = () => (
    <div className="space-y-6">
      {/* Journey Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {currentJourney.recipientInfo.firstName} {currentJourney.recipientInfo.lastName}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {currentJourney.recipientInfo.title} at {currentJourney.recipientInfo.company}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStageColor(currentJourney.journey.stage)}>
                {currentJourney.journey.stage}
              </Badge>
              <Badge className={getStatusColor(currentJourney.journey.status)}>
                {currentJourney.journey.status}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Journey Duration</p>
              <p className="font-semibold">{currentJourney.journey.totalDuration} days</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Engagement Score</p>
              <p className="font-semibold">{currentJourney.metrics.engagementScore}/100</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Touchpoints</p>
              <p className="font-semibold">{currentJourney.touchpoints.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Conversion Likelihood</p>
              <p className="font-semibold">{(currentJourney.predictions.conversionLikelihood * 100).toFixed(0)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Journey Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Journey Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
            
            <div className="space-y-6">
              {currentJourney.touchpoints.map((touchpoint, index) => {
                const Icon = getTouchpointIcon(touchpoint.type);
                const colorClass = getTouchpointColor(touchpoint.type);
                
                return (
                  <div key={touchpoint.id} className="relative flex items-start gap-4">
                    {/* Timeline dot */}
                    <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 ${colorClass.replace('text-', 'border-')}`}>
                      <Icon className={`h-4 w-4 ${colorClass}`} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium capitalize">
                            {touchpoint.type.replace('_', ' ')}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {touchpoint.context.campaignName}
                          </p>
                          {touchpoint.context.subjectLine && (
                            <p className="text-xs text-gray-500 mt-1">
                              "{touchpoint.context.subjectLine}"
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">
                            {formatDate(touchpoint.timestamp)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {touchpoint.metadata.deviceType && (
                              <div className="flex items-center gap-1">
                                {touchpoint.metadata.deviceType === 'mobile' ? (
                                  <Smartphone className="h-3 w-3 text-gray-400" />
                                ) : (
                                  <Monitor className="h-3 w-3 text-gray-400" />
                                )}
                                <span className="text-xs text-gray-500">
                                  {touchpoint.metadata.deviceType}
                                </span>
                              </div>
                            )}
                            {touchpoint.metadata.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500">
                                  {touchpoint.metadata.location}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Additional details */}
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                        <span>Score: {touchpoint.engagement.score}</span>
                        {touchpoint.engagement.duration && (
                          <span>Duration: {formatDuration(touchpoint.engagement.duration)}</span>
                        )}
                        {touchpoint.metadata.conversionValue && (
                          <span className="text-green-600 font-medium">
                            Value: ${touchpoint.metadata.conversionValue.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Predictions & Recommendations */}
      {showPredictions && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              AI Predictions & Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Next Best Action</p>
                  <p className="font-semibold">{currentJourney.predictions.nextBestAction}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Optimal Next Contact</p>
                  <p className="font-semibold">
                    {formatDate(currentJourney.predictions.optimalNextContact)}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversion Likelihood</p>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={currentJourney.predictions.conversionLikelihood * 100} 
                      className="flex-1" 
                    />
                    <span className="font-semibold">
                      {(currentJourney.predictions.conversionLikelihood * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Churn Risk</p>
                  <Badge className={getChurnRiskColor(currentJourney.predictions.churnRisk)}>
                    {currentJourney.predictions.churnRisk}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const AnalyticsView = () => {
    const engagementData = currentJourney.touchpoints.map((tp, index) => ({
      step: index + 1,
      score: tp.engagement.score,
      type: tp.type,
      date: new Date(tp.timestamp).toLocaleDateString()
    }));

    return (
      <div className="space-y-6">
        {/* Engagement Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Email Open Rate</p>
                  <p className="text-2xl font-bold">
                    {((currentJourney.metrics.emailsOpened / currentJourney.metrics.totalEmails) * 100).toFixed(0)}%
                  </p>
                </div>
                <Eye className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Click-through Rate</p>
                  <p className="text-2xl font-bold">
                    {currentJourney.metrics.emailsOpened > 0 
                      ? ((currentJourney.metrics.linksClicked / currentJourney.metrics.emailsOpened) * 100).toFixed(0)
                      : 0}%
                  </p>
                </div>
                <MousePointer className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Response Time</p>
                  <p className="text-2xl font-bold">
                    {currentJourney.metrics.responseTime > 0 
                      ? `${currentJourney.metrics.responseTime.toFixed(1)}h`
                      : 'N/A'}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Engagement Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Engagement Score Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="step" />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => `Touchpoint ${value}`}
                  formatter={(value, name) => [value, 'Engagement Score']}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Behavioral Patterns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Preferred Engagement Times</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Peak Hours</p>
                  <div className="flex gap-2">
                    {currentJourney.patterns.preferredTimes.hours.map(hour => (
                      <Badge key={hour} variant="outline">
                        {hour}:00
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Peak Days</p>
                  <div className="flex gap-2">
                    {currentJourney.patterns.preferredTimes.days.map(day => {
                      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      return (
                        <Badge key={day} variant="outline">
                          {dayNames[day]}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Device & Engagement Patterns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Device Preference</p>
                  <div className="flex items-center gap-2 mt-1">
                    {currentJourney.patterns.devicePreference === 'mobile' && (
                      <Smartphone className="h-4 w-4 text-blue-500" />
                    )}
                    {currentJourney.patterns.devicePreference === 'desktop' && (
                      <Monitor className="h-4 w-4 text-blue-500" />
                    )}
                    <span className="capitalize font-medium">
                      {currentJourney.patterns.devicePreference}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Engagement Trend</p>
                  <div className="flex items-center gap-2 mt-1">
                    {currentJourney.patterns.engagementTrend === 'increasing' && (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    )}
                    {currentJourney.patterns.engagementTrend === 'decreasing' && (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    {currentJourney.patterns.engagementTrend === 'stable' && (
                      <Activity className="h-4 w-4 text-blue-500" />
                    )}
                    <span className="capitalize font-medium">
                      {currentJourney.patterns.engagementTrend}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const SegmentsView = () => (
    <div className="space-y-6">
      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search recipients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Stages</option>
                <option value="awareness">Awareness</option>
                <option value="interest">Interest</option>
                <option value="consideration">Consideration</option>
                <option value="decision">Decision</option>
                <option value="customer">Customer</option>
                <option value="advocate">Advocate</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="converted">Converted</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Journey List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredJourneys.map((journey) => (
          <Card 
            key={journey.recipientId} 
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedJourney?.recipientId === journey.recipientId ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => setSelectedJourney(journey)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    {journey.recipientInfo.firstName} {journey.recipientInfo.lastName}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    {journey.recipientInfo.title} at {journey.recipientInfo.company}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getStageColor(journey.journey.stage)}>
                    {journey.journey.stage}
                  </Badge>
                  <Badge className={getStatusColor(journey.journey.status)}>
                    {journey.journey.status}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Engagement Score</p>
                  <p className="font-semibold">{journey.metrics.engagementScore}/100</p>
                </div>
                <div>
                  <p className="text-gray-600">Touchpoints</p>
                  <p className="font-semibold">{journey.touchpoints.length}</p>
                </div>
                <div>
                  <p className="text-gray-600">Conversion Likelihood</p>
                  <p className="font-semibold">
                    {(journey.predictions.conversionLikelihood * 100).toFixed(0)}%
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Churn Risk</p>
                  <Badge className={getChurnRiskColor(journey.predictions.churnRisk)}>
                    {journey.predictions.churnRisk}
                  </Badge>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Last activity: {formatDate(journey.journey.lastActivity)}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Recipient Journey
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Track and analyze complete customer engagement journeys
          </p>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="segments">All Journeys</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-6">
          <TimelineView />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AnalyticsView />
        </TabsContent>

        <TabsContent value="segments" className="mt-6">
          <SegmentsView />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecipientJourney; 